import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import LogoMark from "./LogoMark";
import { useSite } from "@/context/SiteContext";
import { FLORIDA_NAV } from "@/lib/florida-nav";

const MENU = {
  "Solar & Storage": [
    ["Residential Solar Panels", "/services/solar-panels"],
    ["Commercial Solar", "/services/commercial-solar"],
    ["Tesla Powerwall", "/services/tesla-powerwall"],
    ["Generac Generators & PWRcell 2", "/services/generac-generators"],
  ],
  "Roofing & Construction": [
    ["Tesla Solar Roof Service", "/services/tesla-solar-roof"],
    ["Roofing Services", "/services/roofing-services"],
    ["New Construction Energy Systems", "/services/new-construction"],
  ],
  "EV & Electrical": [
    ["Tesla Powershare", "/services/tesla-powershare"],
    ["SPAN Smart Panel", "/services/span-smart-panel"],
    ["EV Charger Installation", "/services/ev-charger"],
    ["Tesla Supercharger Sites", "/services/tesla-supercharger"],
  ],
  "Comfort & Efficiency": [
    ["HVAC & Indoor Air Quality", "/services/hvac"],
    ["Spray Foam Insulation", "/services/spray-foam-insulation"],
  ],
  "Service & Protection": [
    ["Solar & Battery Service", "/services/service"],
    ["Orphan Solar System Support", "/services/orphan-solar-system"],
    ["Coastal Care Plan", "/services/coastal-care-plan"],
    ["Coastal Protect 30 & Commercial", "/services/solar-insure"],
    ["Workmanship Warranty", "/services/warranty"],
  ],
  "Planning & Financing": [
    ["Financing Options", "/services/financing"],
    ["Solar Tax Strategy Consultation", "/services/solar-depreciation"],
  ],
};

function closeDrop(e) {
  e.currentTarget.closest("details")?.removeAttribute("open");
}

function DropGroups({ groups, more = [] }) {
  return (
    <div className="navdrop-grid" role="menu" aria-label="Menu">
      {groups.map((g) => (
        <div key={g.label}>
          <span className="eyebrow" style={{ fontSize: 10 }}>{g.label}</span>
          {g.items.map(([label, href]) => (
            <Link key={href} to={href} role="menuitem" onClick={closeDrop}>
              {label}
            </Link>
          ))}
          {g.more?.map(([label, href]) => (
            <a key={href} href={href} onClick={closeDrop}>{label}</a>
          ))}
        </div>
      ))}
      {more.length > 0 && (
        <div>
          <span className="eyebrow" style={{ fontSize: 10 }}>MORE</span>
          {more.map(([label, href]) => (
            <Link key={href} to={href} onClick={closeDrop}>{label}</Link>
          ))}
        </div>
      )}
    </div>
  );
}

const SERVICES_MENU = Object.entries(MENU).map(([label, items]) => ({
  label,
  items,
  more: label === "Comfort & Efficiency" ? [["Indoor Air Quality", "/services/hvac#indoor-air-quality"]] : undefined,
}));
const SERVICES_EXTRA = [
  ["All services index", "/services"],
  ["Careers — we're hiring", "/careers"],
  ["Free live quote", "/live-quote"],
];
const CALC_MENU = [
  ["Four-pillar ROI dashboard", "/#roi"],
  ["Florida commercial estimator", "/florida-commercial-solar"],
  ["Roof concept studio", "/roof-visualizer"],
  ["Free live quote", "/live-quote"],
];
const FINANCING_MENU = [
  ["Federal tax incentives review", "/florida-commercial-solar/incentives"],
  ["C-PACE financing", "/florida-commercial-solar/financing"],
  ["Financing options", "/services/financing"],
  ["Solar tax strategy consultation", "/services/solar-depreciation"],
];
const FLORIDA_MENU = FLORIDA_NAV.filter((g) => g.items).map((g) => ({ label: g.label, items: g.items }));

export default function Header() {
  const { config, openAudit } = useSite();
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <header className="site">
      <div className="wrap nav">
        <Link to="/" className="brand" data-testid="brand-link">
          <span className="brandmark">
            <LogoMark size={22} />
          </span>
          <span>
            SOLAR BLACK BELT
            <small>{config.brandLine}</small>
          </span>
        </Link>
        <nav className="navlinks" aria-label="Main navigation">
          <details className="navdrop" data-testid="nav-commercial">
            <summary>
              Commercial Solar <span aria-hidden="true">▾</span>
            </summary>
            <div className="navdrop-panel">
              <DropGroups groups={SERVICES_MENU} more={[["Commercial solar overview", "/services/commercial-solar"], ...SERVICES_EXTRA]} />
            </div>
          </details>
          <details className="navdrop" data-testid="nav-calculators">
            <summary>
              ROI Calculators <span aria-hidden="true">▾</span>
            </summary>
            <div className="navdrop-panel">
              <DropGroups groups={[{ label: "ROI Calculators", items: CALC_MENU }]} />
            </div>
          </details>
          <Link to="/services/tesla-powerwall">Solar + Storage</Link>
          <Link to="/projects">Projects</Link>
          <details className="navdrop" data-testid="nav-florida">
            <summary>
              Florida Service Areas <span aria-hidden="true">▾</span>
            </summary>
            <div className="navdrop-panel">
              <DropGroups groups={[{ label: "Florida Commercial Solar", items: [[FLORIDA_NAV[0].label, FLORIDA_NAV[0].path]] }, ...FLORIDA_MENU]} />
            </div>
          </details>
          <details className="navdrop" data-testid="nav-financing">
            <summary>
              Financing &amp; Incentives <span aria-hidden="true">▾</span>
            </summary>
            <div className="navdrop-panel">
              <DropGroups groups={[{ label: "Financing & Incentives", items: FINANCING_MENU }]} />
            </div>
          </details>
          <Link to="/learn">Learn</Link>
          <Link to="/academy" data-testid="nav-academy">Academy</Link>
          <Link to="/about">About / Contact</Link>
        </nav>
        <div className="navright">
          <a className="phone" href={config.phoneHref} data-testid="header-phone">
            {config.phone}
          </a>
          <button className="btn small" data-testid="header-cta" onClick={() => openAudit()} title="Upload Your Commercial Electric Bill">
            Upload Your Bill <span>↗</span>
          </button>
          <button className="navtoggle" aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen} data-testid="nav-toggle" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? "✕" : "☰"}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <button className="btn lime" data-testid="mobile-upload-cta" onClick={() => { setMobileOpen(false); openAudit(); }}>
            Upload Your Commercial Electric Bill <span>↗</span>
          </button>
          <details className="navdrop" open>
            <summary>
              Commercial Solar <span aria-hidden="true">▾</span>
            </summary>
            <div className="navdrop-panel">
              <DropGroups groups={SERVICES_MENU} more={[["Commercial solar overview", "/services/commercial-solar"], ...SERVICES_EXTRA]} />
            </div>
          </details>
          <details className="navdrop">
            <summary>
              ROI Calculators <span aria-hidden="true">▾</span>
            </summary>
            <div className="navdrop-panel">
              <DropGroups groups={[{ label: "ROI Calculators", items: CALC_MENU }]} />
            </div>
          </details>
          <Link to="/services/tesla-powerwall" onClick={() => setMobileOpen(false)}>Solar + Storage</Link>
          <Link to="/projects" onClick={() => setMobileOpen(false)}>Projects</Link>
          <details className="navdrop">
            <summary>
              Florida Service Areas <span aria-hidden="true">▾</span>
            </summary>
            <div className="navdrop-panel">
              <DropGroups groups={[{ label: "Florida Commercial Solar", items: [[FLORIDA_NAV[0].label, FLORIDA_NAV[0].path]] }, ...FLORIDA_MENU]} />
            </div>
          </details>
          <details className="navdrop">
            <summary>
              Financing &amp; Incentives <span aria-hidden="true">▾</span>
            </summary>
            <div className="navdrop-panel">
              <DropGroups groups={[{ label: "Financing & Incentives", items: FINANCING_MENU }]} />
            </div>
          </details>
          <Link to="/learn" onClick={() => setMobileOpen(false)}>Learn</Link>
          <Link to="/academy" onClick={() => setMobileOpen(false)}>Academy — train for your belts</Link>
          <Link to="/about" onClick={() => setMobileOpen(false)}>About / Contact</Link>
          <Link to="/business" onClick={() => setMobileOpen(false)}>For business</Link>
          <Link to="/homeowners" onClick={() => setMobileOpen(false)}>For homeowners</Link>
          <Link to="/roof-visualizer" onClick={() => setMobileOpen(false)}>See your roof</Link>
          <Link to="/careers" onClick={() => setMobileOpen(false)}>Careers — we're hiring</Link>
        </div>
      )}
    </header>
  );
}
